import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import { stripeService } from '../../services/stripeService'
import { stripeConfig, type TierId, type TierKey } from '../../config/stripe.config'
import { Button, Spinner } from '../../components/common'
import { useToast } from '../../components/common/Toast'

export function SubscriptionPage() {
  const { profile } = useAuthStore()
  const toast = useToast()
  const [searchParams] = useSearchParams()
  const [loadingTier, setLoadingTier] = useState<TierId | null>(null)

  // Check for success/cancelled status from redirect
  const showSuccess = searchParams.get('subscription') === 'success'
  const showCancelled = searchParams.get('cancelled') === 'true'

  // currentTierId is lowercase for stripeService functions
  const currentTierId = (profile?.subscription_tier?.toLowerCase() || 'free') as TierId
  // currentTierKey is uppercase for accessing stripeConfig.tiers
  const currentTierKey = currentTierId.toUpperCase() as TierKey

  const handleSubscribe = async (tierId: TierId) => {
    if (!profile) return

    // Don't allow downgrade through this flow
    if (!stripeService.isUpgrade(currentTierId, tierId)) {
      toast.info('Please use the customer portal to manage or downgrade your subscription')
      return
    }

    setLoadingTier(tierId)

    try {
      // If user already has a subscription, use Customer Portal for upgrades
      // This ensures proper proration instead of charging full amount
      if (profile.stripe_subscription_id) {
        const portalUrl = await stripeService.openCustomerPortal(profile.stripe_customer_id!)
        if (portalUrl) {
          window.location.href = portalUrl
        } else {
          toast.error('Failed to open subscription management')
        }
      } else {
        // New subscriber - use Stripe Checkout
        await stripeService.redirectToCheckout(tierId, profile.id)
      }
    } catch (error) {
      console.error('Checkout error:', error)
      toast.error('Failed to start checkout. Please try again.')
    } finally {
      setLoadingTier(null)
    }
  }

  const handleManageSubscription = async () => {
    if (!profile?.stripe_customer_id) {
      toast.error('No active subscription to manage')
      return
    }

    const portalUrl = await stripeService.openCustomerPortal(profile.stripe_customer_id)
    if (portalUrl) {
      window.location.href = portalUrl
    } else {
      toast.error('Failed to open subscription management')
    }
  }

  const tiers = Object.entries(stripeConfig.tiers) as [TierKey, typeof stripeConfig.tiers[TierKey]][]

  return (
    <div className="space-y-8">
      {/* Success/cancelled banners */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <div>
              <p className="font-medium text-green-800">Subscription activated!</p>
              <p className="text-sm text-green-600">
                Your account has been upgraded. Enjoy your new features!
              </p>
            </div>
          </div>
        </div>
      )}

      {showCancelled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg
              className="w-6 h-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p className="font-medium text-yellow-800">Checkout cancelled</p>
              <p className="text-sm text-yellow-600">
                No changes were made to your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
        <p className="mt-1 text-gray-600">
          Choose the plan that works best for you and your practice.
        </p>
      </div>

      {/* Current plan */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
            <div className="mt-2 flex items-center gap-3">
              <span className="text-2xl font-bold text-gray-900">
                {stripeConfig.tiers[currentTierKey]?.name || 'Free'}
              </span>
              {profile?.subscription_status === 'active' && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  Active
                </span>
              )}
              {profile?.subscription_status === 'canceling' && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
                  Canceling
                </span>
              )}
              {profile?.subscription_status === 'past_due' && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                  Past Due
                </span>
              )}
            </div>
            {profile?.subscription_status === 'canceling' && (
              <p className="mt-2 text-sm text-yellow-700">
                Your subscription is scheduled to cancel at the end of your billing period.
                You'll retain access to all features until then.
              </p>
            )}
          </div>
          {profile?.stripe_customer_id && (
            <Button variant="outline" onClick={handleManageSubscription}>
              Manage Subscription
            </Button>
          )}
        </div>
      </div>

      {/* Pricing grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {tiers.map(([tierKey, tier]) => {
          // tier.id is lowercase (TierId), tierKey is uppercase (TierKey)
          const tierId = tier.id
          const isCurrent = tierKey === currentTierKey
          const isUpgrade = stripeService.isUpgrade(currentTierId, tierId)

          return (
            <div
              key={tierKey}
              className={`bg-white rounded-xl border-2 p-6 relative ${
                isCurrent
                  ? 'border-blue-500 ring-2 ring-blue-100'
                  : 'border-gray-200'
              }`}
            >
              {isCurrent && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                  Current Plan
                </span>
              )}

              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {tier.name}
                </h3>
                <div className="mt-2">
                  {tier.price === null ? (
                    <span className="text-2xl font-bold text-gray-900">
                      Custom
                    </span>
                  ) : tier.price === 0 ? (
                    <span className="text-2xl font-bold text-gray-900">Free</span>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">
                        ${tier.price}
                      </span>
                      <span className="text-gray-500">/mo</span>
                    </>
                  )}
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {tier.features.map((feature: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <Button fullWidth variant="outline" disabled>
                  Current Plan
                </Button>
              ) : tier.price === null ? (
                <Button
                  fullWidth
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:sales@exercisetoolbox.com'}
                >
                  Contact Sales
                </Button>
              ) : isUpgrade ? (
                <Button
                  fullWidth
                  onClick={() => handleSubscribe(tierId)}
                  disabled={loadingTier !== null || !stripeConfig.getPriceId(tierId)}
                >
                  {loadingTier === tierId ? (
                    <Spinner size="sm" />
                  ) : !stripeConfig.getPriceId(tierId) ? (
                    'Coming Soon'
                  ) : (
                    'Upgrade'
                  )}
                </Button>
              ) : tier.price === 0 ? (
                // Free tier - must cancel subscription to return to free
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={!profile?.stripe_customer_id}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={!profile?.stripe_customer_id}
                >
                  Downgrade via Portal
                </Button>
              )}
            </div>
          )
        })}
      </div>

      {/* FAQ */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Frequently Asked Questions
        </h2>
        <dl className="space-y-4">
          <div>
            <dt className="font-medium text-gray-900">
              Can I change my plan later?
            </dt>
            <dd className="mt-1 text-gray-600">
              Yes! You can upgrade at any time with prorated billing. To downgrade
              to a lower paid tier, use the Customer Portal. Downgrades take effect
              at the end of your current billing period.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">
              How do I cancel my subscription?
            </dt>
            <dd className="mt-1 text-gray-600">
              Click "Cancel Subscription" on the Free tier card, then confirm in
              the Customer Portal. Your paid features remain active until the end
              of your billing period, then you'll return to the Free plan.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">
              What happens to my videos if I downgrade?
            </dt>
            <dd className="mt-1 text-gray-600">
              Your uploaded videos remain available, but you won't be able to
              upload new ones until you upgrade again.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-900">
              Is there a free trial?
            </dt>
            <dd className="mt-1 text-gray-600">
              The Free plan lets you browse and assign public videos. Upgrade
              anytime to unlock uploads and more features.
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
